import { SUPPORTED_LOCALES_OBJ } from '@zep-us/i18n';
import {
  type GetServerSidePropsContext,
  type GetStaticPropsContext,
} from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { type LoadableNamespace } from '@/types/translation';

export const THIS_IS_DEFAULT_STATIC_OR_SERVER_SIDE_PROPS =
  'THIS_IS_DEFAULT_STATIC_OR_SERVER_SIDE_PROPS';

/**
 * 정적 페이지의 CDN 캐시 TTL (초).
 *
 * 왜 600인가:
 *   CloudFront(셀프호스팅, ZEP-591) 는 Vercel 과 달리 배포 시 자동 캐시 퍼지가 없다.
 *   `revalidate` 없으면 Next.js 가 s-maxage=31536000(1년)을 내보내
 *   배포 후에도 최대 1년간 구 HTML이 서빙된다(실사고: /ko age:158574 ≈ 44시간).
 *   1차 방어는 배포 트리거 시 a-z invalidation(eks#135)이지만,
 *   잡 실패 시 자가 회복 보험으로 캐시 TTL 상한을 10분으로 캡한다.
 *   600초는 setPublicPageCache의 user 프로필(s-maxage=600)과 동일한 값으로,
 *   신선도와 오리진 재조리 부하 간 트레이드오프 지점으로 합의된 값이다.
 *
 * 왜 BUILD_STANDALONE 게이트(조건부) 없이 양쪽 빌드 동일한가:
 *   Vercel 빌드와 EKS 이미지 빌드가 서로 다른 revalidate를 가지면
 *   카나리 두 버킷의 캐시 동작이 갈라져 비교성이 깨진다.
 *   실사례: 43시간 빌드 드리프트가 카나리 측정을 오염했다(ZEP-591).
 *   또한 Vercel도 ISR revalidation 이 동작해야 스테이지 테스트가 prod 를 근사한다.
 */
export const STATIC_PAGE_REVALIDATE_SECONDS = 600;

type StaticProps = Pick<GetStaticPropsContext, 'locale'> & {
  namespaces?: LoadableNamespace[];
  extraLocales?: string[];
};

type ServerSideProps = Pick<GetServerSidePropsContext, 'locale'> & {
  namespaces?: LoadableNamespace[];
};

export const getDefaultServerSideProps = async ({
  locale = SUPPORTED_LOCALES_OBJ.en,
  namespaces = ['common'],
}: ServerSideProps) => {
  // TODO: common -> seo 시트로 변경
  if (!namespaces.includes('common')) {
    namespaces.push('common');
  }
  return {
    props: {
      ...(await serverSideTranslations(locale, namespaces)),
      [THIS_IS_DEFAULT_STATIC_OR_SERVER_SIDE_PROPS]: true,
    },
  };
};

export const getDefaultStaticProps = async ({
  locale = SUPPORTED_LOCALES_OBJ.en,
  namespaces = ['common'],
  extraLocales,
}: StaticProps) => {
  // TODO: common -> seo 시트로 변경
  if (!namespaces.includes('common')) {
    namespaces.push('common');
  }
  return {
    props: {
      ...(await serverSideTranslations(
        locale,
        namespaces,
        undefined,
        extraLocales,
      )),
      [THIS_IS_DEFAULT_STATIC_OR_SERVER_SIDE_PROPS]: true,
    },
    revalidate: STATIC_PAGE_REVALIDATE_SECONDS,
  };
};
